import { defineStore } from 'pinia'
import { getConfig, saveConfig } from 'src/service/config.global'
import { ref, watch } from 'vue'
import { useAuthStore } from './auth.store'

const STORAGE_KEY = 'cf_global_config'
const HASH_KEY = 'cf_global_config_hash'

export const useGlobalConfigStore = defineStore('global.config', () => {
  const hash = ref<string>()
  const globalConfig = ref<Dictionary<any>>({})
  const featureFlags = ref<Record<string, boolean>>({})
  const lastUpdated = ref<Date>()
  const refreshInterval = ref<number>()
  const isInitialized = ref(false)
  let pendingRefresh: Promise<void> | null = null

  const authStore = useAuthStore()

  // Load persisted config on initialization
  loadPersistedConfig()

  // Watch for auth state changes
  watch(
    () => authStore.isAuthenticated,
    async (isAuthenticated) => {
      if (isAuthenticated && !isInitialized.value) {
        // User just logged in, initialize the store
        await initialize()
      } else if (!isAuthenticated && isInitialized.value) {
        // User logged out, clean up
        stopPeriodicRefresh()
        isInitialized.value = false
        hash.value = undefined
        globalConfig.value = {}
        featureFlags.value = {}
        lastUpdated.value = undefined
        // Clear persisted config on logout
        try {
          localStorage.removeItem(STORAGE_KEY)
          localStorage.removeItem(HASH_KEY)
        } catch {
          // Ignore
        }
      }
    },
    { immediate: true },
  )

  /**
   * Initialize the store and start periodic refresh
   * Called once during app boot or when user logs in
   */
  async function initialize() {
    if (isInitialized.value) return
    if (!authStore.isAuthenticated) return // Only initialize if authenticated

    // Initial load
    await refresh()
    isInitialized.value = true

    // Start periodic refresh every 5 minutes
    startPeriodicRefresh()
  }

  /**
   * Start periodic refresh interval
   */
  function startPeriodicRefresh() {
    if (refreshInterval.value) return

    refreshInterval.value = setInterval(
      () => {
        void refresh()
      },
      5 * 60 * 1000,
    ) as unknown as number
  }

  /**
   * Stop periodic refresh interval
   */
  function stopPeriodicRefresh() {
    if (refreshInterval.value) {
      clearInterval(refreshInterval.value)
      refreshInterval.value = undefined
    }
  }

  function get<T>(key: string, defaultValue?: T) {
    if (globalConfig.value[key]) return globalConfig.value[key] as T
    return defaultValue
  }

  async function set<T>(key: string, value: T) {
    if (!authStore.isSuperAdmin)
      throw new Error(
        'Access denied: insufficient permissions to modify org configuration',
      )
    globalConfig.value[key] = value
    const response = await saveConfig(globalConfig.value)
    hash.value = response.hash
    globalConfig.value = response.config
    lastUpdated.value = new Date()
    persistConfig()
  }

  async function remove(key: string) {
    if (!authStore.isSuperAdmin)
      throw new Error(
        'Access denied: insufficient permissions to modify org configuration',
      )
    delete globalConfig.value[key]
    const response = await saveConfig(globalConfig.value)
    hash.value = response.hash
    globalConfig.value = response.config
    lastUpdated.value = new Date()
    persistConfig()
  }

  // Load persisted config from localStorage
  function loadPersistedConfig() {
    try {
      const persistedConfig = localStorage.getItem(STORAGE_KEY)
      const persistedHash = localStorage.getItem(HASH_KEY)

      if (persistedConfig && persistedHash) {
        const parsed = JSON.parse(persistedConfig)
        const { feature_flags, ...otherConfig } = parsed

        globalConfig.value = otherConfig
        featureFlags.value = (feature_flags as Record<string, boolean>) || {}
        hash.value = persistedHash
      }
    } catch (error) {
      // Silently fail - persistence is not critical
      if (process.env.DEV) {
        console.error(
          '[GlobalConfigStore] Failed to load persisted config:',
          error,
        )
      }
    }
  }

  // Persist config to localStorage
  function persistConfig() {
    try {
      const combinedConfig = {
        ...globalConfig.value,
        feature_flags: featureFlags.value,
      }
      localStorage.setItem(STORAGE_KEY, JSON.stringify(combinedConfig))
      if (hash.value) {
        localStorage.setItem(HASH_KEY, hash.value)
      }
    } catch (error) {
      // Silently fail - persistence is not critical
      if (process.env.DEV) {
        console.error('[GlobalConfigStore] Failed to persist config:', error)
      }
    }
  }

  async function refresh() {
    // Only refresh if user is authenticated
    if (!authStore.isAuthenticated) return

    // Dedupe concurrent refresh calls (e.g. the auth watcher and an explicit
    // caller both firing at boot) into a single in-flight request
    if (pendingRefresh) return pendingRefresh

    pendingRefresh = (async () => {
      try {
        const response = await getConfig()
        hash.value = response.hash

        // Separate feature flags from general config
        const { feature_flags, ...otherConfig } = response.config
        globalConfig.value = otherConfig
        featureFlags.value = (feature_flags as Record<string, boolean>) || {}

        lastUpdated.value = new Date()

        // Persist to localStorage after successful refresh
        persistConfig()
      } catch (error) {
        // Silently fail - config refresh is not critical
        if (process.env.DEV) {
          console.error('[GlobalConfigStore] Failed to refresh config:', error)
        }
      } finally {
        pendingRefresh = null
      }
    })()

    return pendingRefresh
  }

  // Feature Flag Management
  function getFlag(key: string): boolean {
    return featureFlags.value[key] === true
  }

  function getAllFlags(): Record<string, boolean> {
    return { ...featureFlags.value }
  }

  async function setFlag(key: string, value: boolean) {
    if (!authStore.isSuperAdmin)
      throw new Error(
        'Access denied: insufficient permissions to modify feature flags',
      )

    featureFlags.value[key] = value

    // Save combined config back to server
    const combinedConfig = {
      ...globalConfig.value,
      feature_flags: featureFlags.value,
    }

    const response = await saveConfig(combinedConfig)
    hash.value = response.hash

    // Re-separate on response
    const { feature_flags, ...otherConfig } = response.config
    globalConfig.value = otherConfig
    featureFlags.value = (feature_flags as Record<string, boolean>) || {}

    lastUpdated.value = new Date()
    persistConfig()
  }

  async function removeFlag(key: string) {
    if (!authStore.isSuperAdmin)
      throw new Error(
        'Access denied: insufficient permissions to modify feature flags',
      )

    // Create new object without the key (more reactive)
    const { [key]: _removed, ...remainingFlags } = featureFlags.value
    featureFlags.value = remainingFlags

    // Save combined config back to server
    const combinedConfig = {
      ...globalConfig.value,
      feature_flags: featureFlags.value,
    }

    const response = await saveConfig(combinedConfig)
    hash.value = response.hash

    // Re-separate on response
    const { feature_flags, ...otherConfig } = response.config
    globalConfig.value = otherConfig
    featureFlags.value = (feature_flags as Record<string, boolean>) || {}

    lastUpdated.value = new Date()
    persistConfig()
  }

  async function setAllFlags(flags: Record<string, boolean>) {
    if (!authStore.isSuperAdmin)
      throw new Error(
        'Access denied: insufficient permissions to modify feature flags',
      )

    featureFlags.value = { ...flags }

    // Save combined config back to server
    const combinedConfig = {
      ...globalConfig.value,
      feature_flags: featureFlags.value,
    }

    const response = await saveConfig(combinedConfig)
    hash.value = response.hash

    // Re-separate on response
    const { feature_flags, ...otherConfig } = response.config
    globalConfig.value = otherConfig
    featureFlags.value = (feature_flags as Record<string, boolean>) || {}

    lastUpdated.value = new Date()
    persistConfig()
  }

  return {
    // General config
    config: globalConfig,
    get,
    set,
    remove,
    refresh,
    initialize,
    stopPeriodicRefresh,
    isInitialized,
    lastUpdated,

    // Feature flags
    featureFlags,
    getFlag,
    getAllFlags,
    setFlag,
    removeFlag,
    setAllFlags,
  }
})
